import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalInput } from "@/components/ui/BrutalInput";
import {
  BrutalSelect,
  BrutalSelectContent,
  BrutalSelectItem,
  BrutalSelectTrigger,
  BrutalSelectValue,
} from "@/components/ui/BrutalSelect";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RichTextEditor } from "@/components/editor/RichTextEditor";
import { PartnershipImageUpload } from "@/components/admin/PartnershipImageUpload";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface Partnership {
  id: string;
  name: string;
  image_url: string | null;
  description_it: string | null;
  description_en: string | null;
  description_es: string | null;
  discount_code: string | null;
  redemption_url: string | null;
  category: string | null;
  is_active: boolean;
  display_order: number;
}

interface PartnershipFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  partnership: Partnership | null;
}

const CATEGORY_KEYS = ["software", "formazione", "servizi", "prodotti", "altro"];

export function PartnershipFormModal({
  open,
  onClose,
  onSuccess,
  partnership,
}: PartnershipFormModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("it");

  const [formData, setFormData] = useState({
    name: "",
    image_url: "",
    description_it: "",
    description_en: "",
    description_es: "",
    discount_code: "",
    redemption_url: "",
    category: "altro",
    is_active: true,
    display_order: 0,
  });

  useEffect(() => {
    if (partnership) {
      setFormData({
        name: partnership.name || "",
        image_url: partnership.image_url || "",
        description_it: partnership.description_it || "",
        description_en: partnership.description_en || "",
        description_es: partnership.description_es || "",
        discount_code: partnership.discount_code || "",
        redemption_url: partnership.redemption_url || "",
        category: partnership.category || "altro",
        is_active: partnership.is_active,
        display_order: partnership.display_order || 0,
      });
    } else {
      setFormData({
        name: "",
        image_url: "",
        description_it: "",
        description_en: "",
        description_es: "",
        discount_code: "",
        redemption_url: "",
        category: "altro",
        is_active: true,
        display_order: 0,
      });
    }
  }, [partnership, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast({
        title: t("common.error"),
        description: t("admin.partnerships.nameRequired"),
        variant: "error",
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        created_by: partnership ? undefined : user?.id,
      };

      if (partnership) {
        const { error } = await supabase
          .from("partnerships")
          .update(payload)
          .eq("id", partnership.id);
        if (error) throw error;
        toast({ title: t("admin.partnerships.updated") });
      } else {
        const { error } = await supabase.from("partnerships").insert(payload);
        if (error) throw error;
        toast({ title: t("admin.partnerships.created") });
      }
      onSuccess();
    } catch (error: any) {
      toast({
        title: t("common.error"),
        description: error.message,
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto border-[3px] border-dark shadow-[6px_6px_0px_0px] shadow-dark">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {partnership
              ? t("admin.partnerships.edit")
              : t("admin.partnerships.create")}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Nome */}
          <div className="space-y-2">
            <Label>{t("admin.partnerships.name")} *</Label>
            <BrutalInput
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={t("admin.partnerships.namePlaceholder")}
              required
            />
          </div>

          {/* Immagine */}
          <div className="space-y-2">
            <Label>{t("admin.partnerships.image")}</Label>
            <PartnershipImageUpload
              value={formData.image_url}
              onChange={(url) => setFormData({ ...formData, image_url: url })}
            />
          </div>

          {/* Descrizione multilingua */}
          <div className="space-y-2">
            <Label>{t("admin.partnerships.description")}</Label>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="border-[3px] border-dark">
                <TabsTrigger value="it">🇮🇹 IT</TabsTrigger>
                <TabsTrigger value="en">🇬🇧 EN</TabsTrigger>
                <TabsTrigger value="es">🇪🇸 ES</TabsTrigger>
              </TabsList>
              <TabsContent value="it" className="mt-2">
                <RichTextEditor
                  value={formData.description_it}
                  onChange={(html) => setFormData({ ...formData, description_it: html })}
                  placeholder={t("admin.partnerships.descriptionPlaceholderIt")}
                />
              </TabsContent>
              <TabsContent value="en" className="mt-2">
                <RichTextEditor
                  value={formData.description_en}
                  onChange={(html) => setFormData({ ...formData, description_en: html })}
                  placeholder={t("admin.partnerships.descriptionPlaceholderEn")}
                />
              </TabsContent>
              <TabsContent value="es" className="mt-2">
                <RichTextEditor
                  value={formData.description_es}
                  onChange={(html) => setFormData({ ...formData, description_es: html })}
                  placeholder={t("admin.partnerships.descriptionPlaceholderEs")}
                />
              </TabsContent>
            </Tabs>
          </div>

          {/* Codice sconto */}
          <div className="space-y-2">
            <Label>{t("admin.partnerships.discountCode")}</Label>
            <BrutalInput
              value={formData.discount_code}
              onChange={(e) => setFormData({ ...formData, discount_code: e.target.value })}
              placeholder={t("admin.partnerships.discountCodePlaceholder")}
            />
          </div>

          {/* URL riscatto */}
          <div className="space-y-2">
            <Label>{t("admin.partnerships.redemptionUrl")}</Label>
            <BrutalInput
              value={formData.redemption_url}
              onChange={(e) => setFormData({ ...formData, redemption_url: e.target.value })}
              placeholder={t("admin.partnerships.redemptionUrlPlaceholder")}
              type="url"
            />
          </div>

          {/* Categoria */}
          <div className="space-y-2">
            <Label>{t("admin.partnerships.category")}</Label>
            <BrutalSelect value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
              <BrutalSelectTrigger>
                <BrutalSelectValue />
              </BrutalSelectTrigger>
              <BrutalSelectContent>
                {CATEGORY_KEYS.map((key) => (
                  <BrutalSelectItem key={key} value={key}>
                    {t(`admin.partnerships.categories.${key}`)}
                  </BrutalSelectItem>
                ))}
              </BrutalSelectContent>
            </BrutalSelect>
          </div>

          {/* Ordine */}
          <div className="space-y-2">
            <Label>{t("admin.partnerships.order")}</Label>
            <BrutalInput
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
              min={0}
            />
          </div>

          {/* Attivo */}
          <div className="flex items-center justify-between p-4 border-[3px] border-dark rounded-none">
            <Label>{t("admin.partnerships.isActive")}</Label>
            <Switch
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <BrutalButton type="button" variant="outline" onClick={onClose} className="flex-1">
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton type="submit" disabled={loading} className="flex-1">
              {loading ? "..." : partnership ? t("common.save") : t("common.create")}
            </BrutalButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
